import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import Navbar from "../Components/Navbar";
import useAddProduct from "../hooks/useAddProduct";

const AddProducts = () => {
  const {
    formData,
    handleChange,
    handleFileChange,
    handleSubmit,
    loading,
    error,
    successMessage,
    resetFileInput,
  } = useAddProduct();

  const [isFileInputFocused, setIsFileInputFocused] = useState(false);
  const [hasFile, setHasFile] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 2 * 1024 * 1024) {
      alert("File size exceeds 2MB limit!");
      return;
    }
    handleFileChange(e);
    setHasFile(!!file);
    if (file) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };

  useEffect(() => {
    if (resetFileInput) {
      setHasFile(false);
      setPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [resetFileInput, preview]);

  const handleCloseModal = () => {
    window.location.reload();
  };

  return (
    <PageContainer>
      <Navbar />
      <Container>
        <Content>
          <AnimatedLeaf />
          <Title>ADD PRODUCTS</Title>
          <FormContainer onSubmit={handleSubmit} encType="multipart/form-data">
            <SectionTitle>Add a New Product</SectionTitle>
            <RadialForm>
              <Row>
                <InputWrapper>
                  <Label className={formData.seller_name ? "floating" : ""}>
                    Seller Full Name<span className="required">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="seller_name"
                    value={formData.seller_name}
                    onChange={handleChange}
                    placeholder=" "
                    required
                  />
                </InputWrapper>
                <InputWrapper>
                  <Label className={formData.product_name ? "floating" : ""}>
                    Product Name<span className="required">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleChange}
                    placeholder=" "
                    required
                  />
                </InputWrapper>
              </Row>

              <Row>
                <InputWrapper>
                  <Label className={formData.quantity ? "floating" : ""}>
                    Quantity<span className="required">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    min="0"
                  />
                </InputWrapper>
                <InputWrapper>
                  <Label className={formData.category ? "floating" : ""}>
                    Category<span className="required">*</span>
                  </Label>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value=""></option>
                    <option value="Vegetables">Vegetables</option>
                    <option value="Fruits">Fruits</option>
                    <option value="Food">Food</option>
                  </Select>
                </InputWrapper>
              </Row>

              <Row>
                <InputWrapper>
                  <Label className={formData.unit_type ? "floating" : ""}>
                    Unit Type<span className="required">*</span>
                  </Label>
                  <Select
                    name="unit_type"
                    value={formData.unit_type}
                    onChange={handleChange}
                    required
                  >
                    <option value=""></option>
                    <option value="kilo">Kilo</option>
                    <option value="pcs">Pieces (pcs)</option>
                  </Select>
                </InputWrapper>
                <InputWrapper>
                  <Label className={formData.price ? "floating" : ""}>
                    Price<span className="required">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    min="0"
                  />
                </InputWrapper>
              </Row>

              <Row>
                <InputWrapper>
                  <Label
                    className={isFileInputFocused || hasFile ? "floating" : ""}
                  >
                  </Label>
                  <FileInput
                    type="file"
                    name="image"
                    accept="image/jpeg,image/png,image/gif"
                    onChange={onFileChange}
                    onFocus={() => setIsFileInputFocused(true)}
                    onBlur={() => setIsFileInputFocused(false)}
                    ref={fileInputRef}
                    required
                    hidden
                  />
                  <FileInputButton
                    type="button"
                    onClick={handleFileInputClick}
                    isFocused={isFileInputFocused}
                    hasFile={hasFile}
                  >
                    {hasFile ? "Image Selected" : "Choose Image"}
                  </FileInputButton>
                  {preview && <ImagePreview src={preview} alt="Preview" />}
                </InputWrapper>
                <InputWrapper>
                  <Label className={formData.description ? "floating" : ""}>
                    Description<span className="required">*</span>
                  </Label>
                  <TextArea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder=" "
                    required
                  />
                </InputWrapper>
              </Row>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? "Adding Product..." : "Add Product"}
              </SubmitButton>

              {loading && <LoadingSpinner>Loading...</LoadingSpinner>}
              {error && (
                <Modal>
                  <ModalContent isError={true}>
                    <ErrorIcon>✖</ErrorIcon>
                    <ModalTitle isError={true}>Error</ModalTitle>
                    <ModalMessage>{error}</ModalMessage>
                    <ModalButton onClick={handleCloseModal}>Try Again</ModalButton>
                  </ModalContent>
                </Modal>
              )}
              {successMessage && (
                <Modal>
                  <ModalContent>
                    <SuccessIcon>✔</SuccessIcon>
                    <ModalTitle>Success</ModalTitle>
                    <ModalMessage>{successMessage}</ModalMessage>
                    <ModalButton onClick={handleCloseModal}>Continue</ModalButton>
                  </ModalContent>
                </Modal>
              )}
            </RadialForm>
          </FormContainer>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default AddProducts;

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #eaf4e1;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
  flex-grow: 1;

  @media (max-width: 768px) {
    padding: 1.5rem 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.25rem;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 900px;
  background: rgba(255, 255, 255, 0.9);
  padding: 2.5rem;
  border-radius: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  border: 2px solid #4CAF50;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const AnimatedLeaf = styled.div`
  position: absolute;
  top: -50px;
  left: -50px;
  width: 100px;
  height: 100px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%234CAF50"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/></svg>') no-repeat center;
  background-size: contain;
  animation: float 6s ease-in-out infinite;

  @keyframes float {
    0% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(10deg); }
    100% { transform: translateY(0) rotate(0deg); }
  }
`;

const Title = styled.h1`
  color: #2E7D32;
  text-align: center;
  font-size: clamp(2rem, 5vw, 2.5rem);
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  font-family: 'Georgia', serif;

  @media (max-width: 480px) {
    font-size: clamp(1.5rem, 4vw, 2rem);
  }
`;

const FormContainer = styled.form`
  width: 100%;
`;

const RadialForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background:  linear-gradient(45deg, #4CAF50,rgb(30, 103, 34));
  padding: 2rem;
  border-radius: 20px;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(76, 175, 80, 0.2) 0%, transparent 70%);
    z-index: 0;
    animation: rotate 10s linear infinite;
  }

  @keyframes rotate {
    100% { transform: rotate(1turn); }
  }
`;

const SectionTitle = styled.h2`
  color: #2E7D32;
  font-size: clamp(1.5rem, 4vw, 1.75rem);
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: 'Georgia', serif;

  @media (max-width: 480px) {
    font-size: clamp(1.25rem, 3.5vw, 1.5rem);
  }
`;

const Form = styled.div``; // Replaced with RadialForm

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  padding: 0.5rem;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const Label = styled.label`
  position: absolute;
  left: 1rem;
  top: 0.75rem;
  color: #666;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 1;

  &.floating {
    top: -0.5rem;
    left: 0.75rem;
    font-size: clamp(0.75rem, 2vw, 0.8rem);
    color: #2E7D32;
    background: rgba(255, 255, 255, 0.9);
    padding: 0 0.25rem;
    border-radius: 3px;
  }

  .required {
    color: #D32F2F;
    margin-left: 0.125rem;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.85rem, 2.5vw, 0.9rem);

    &.floating {
      font-size: clamp(0.7rem, 2vw, 0.75rem);
    }
  }
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 10px;
  width: 100%;
  box-sizing: border-box;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  background: rgba(255, 255, 255, 0.95);
  outline: none;

  &:focus {
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.65rem 0.85rem;
    font-size: clamp(0.85rem, 2.5vw, 0.9rem);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 10px;
  width: 100%;
  box-sizing: border-box;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  outline: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.95) url('data:image/svg+xml;utf8,<svg fill="%232E7D32" height="24" viewBox="0 0 24 24" width="24" xmlns="https://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>') no-repeat right 0.75rem center;
  background-size: 1.25rem;

  &:focus {
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.65rem 0.85rem;
    font-size: clamp(0.85rem, 2.5vw, 0.9rem);
    background-size: 1rem;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputButton = styled.button`
  padding: 0.75rem 1rem;
  border: 2px dashed #4CAF50;
  border-radius: 10px;
  width: 100%;
  box-sizing: border-box;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  background-color: #E8F5E9;
  color: #2E7D32;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2E7D32;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
    outline: none;
  }

  @media (max-width: 480px) {
    padding: 0.65rem 0.85rem;
    font-size: clamp(0.85rem, 2.5vw, 0.9rem);
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  height: auto;
  margin-top: 0.5rem;
  border-radius: 10px;
  border: 2px solid #4CAF50;
  display: block;

  @media (max-width: 480px) {
    margin-top: 0.25rem;
  }
`;

const TextArea = styled.textarea`
  height: 6rem;
  padding: 1rem;
  border: none;
  border-radius: 10px;
  width: 100%;
  box-sizing: border-box;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  background: rgba(255, 255, 255, 0.95);
  outline: none;
  resize: vertical;

  &:focus {
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.85rem;
    font-size: clamp(0.85rem, 2.5vw, 0.9rem);
    height: 5rem;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  color: #2E7D32;
  margin-top: 0.5rem;

  &::before {
    content: "";
    width: 1.5rem;
    height: 1.5rem;
    border: 2px solid #2E7D32;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.85rem, 2.5vw, 0.9rem);

    &::before {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #4CAF50, rgb(30, 103, 34));
  color: white;
  padding: 0.9rem;
  border: 2px solid white;
  border-radius: 20px;
  cursor: pointer;
  font-size: clamp(1rem, 3vw, 1.1rem);
  font-weight: bold;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-top: 1.5rem;
  position: relative;
  overflow: hidden;

  /* Shimmer effect */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: shimmer 2s infinite;
    transform: skewX(-20deg);
  }

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(76, 175, 80, 0.5);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: clamp(0.9rem, 2.5vw, 1rem);
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #E8F5E9;
  padding: 2.5rem;
  border-radius: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 90%;
  max-width: 450px;
  text-align: center;
  border: 2px solid #4CAF50;
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #4CAF50, #66BB6A);
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
`;

const ErrorIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #D32F2F, #EF5350);
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  box-shadow: 0 0 10px rgba(211, 47, 47, 0.5);
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: bold;
  color: ${({ isError }) => (isError ? "#D32F2F" : "#2E7D32")};
  margin: 0;
  font-family: 'Georgia', serif;
`;

const ModalMessage = styled.p`
  font-size: 1.1rem;
  color: #444;
  margin: 0;
`;

const ModalButton = styled.button`
  background: linear-gradient(45deg, #4CAF50, #81C784);
  color: white;
  padding: 0.9rem 2.5rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: bold;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(76, 175, 80, 0.5);
  }
`; 